import React, { useState } from "react";
import { api, errMsg } from "@finance/lib/api";
import { toast } from "sonner";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { Plus } from "@phosphor-icons/react";

const BRANDS = ["Multotec", "Condra", "Dromex", "Elephant Lifting", "Nordbak", "Others"];
const ACCT_TYPES = ["Asset", "Liability", "Equity", "Income", "Expense"];

const CONFIG = {
  client: {
    title: "New Creditor / Client",
    endpoint: "/clients",
    fields: [["name", "Name", true], ["tin", "TIN"], ["phone", "Phone"], ["email", "Email"]],
  },
  supplier: {
    title: "New Supplier",
    endpoint: "/suppliers",
    fields: [["name", "Name", true], ["tin", "TIN"], ["phone", "Phone"], ["email", "Email"]],
  },
  item: {
    title: "New Inventory Item",
    endpoint: "/items",
    fields: [["sku", "SKU / Item Code", true], ["name", "Name / Description", true], ["brand", "Brand", true, "brand"], ["manufacturer", "Manufacturer Code"], ["item_type", "Type (Goods / Service)", false, "itemtype"], ["unit", "Unit"], ["opening_qty", "Opening Qty", false, "number"], ["opening_unit_cost", "Opening Unit Cost (TZS)", false, "number"], ["opening_date", "Opening Date", false, "date"]],
  },
  account: {
    title: "New GL Account",
    endpoint: "/gl/accounts",
    fields: [["code", "Account Code", true], ["name", "Account Name", true], ["type", "Type", true, "acctype"]],
  },
  currency: {
    title: "New Currency",
    endpoint: "/currencies",
    fields: [["code", "Code (e.g. EUR)", true], ["symbol", "Symbol"], ["name", "Name"]],
  },
};

// Inline "+" button + dialog to create a client, supplier, or item without leaving the flow.
export function QuickAddButton({ type, onCreated, size = "icon" }) {
  const cfg = CONFIG[type];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    for (const [k, label, req] of cfg.fields) {
      if (req && !form[k]) { toast.error(`${label} is required`); return; }
    }
    setSaving(true);
    try {
      let payload;
      if (type === "item") payload = { unit: "unit", brand: "Others", item_type: "Goods", ...form, opening_qty: form.opening_qty ? Number(form.opening_qty) : null, opening_unit_cost: form.opening_unit_cost ? Number(form.opening_unit_cost) : null };
      else if (type === "account") payload = { ...form, code: (form.code || "").trim(), type: form.type || "Expense", normal_balance: ["Asset", "Expense"].includes(form.type || "Expense") ? "debit" : "credit", active: true, editable: true };
      else if (type === "currency") payload = { ...form, code: (form.code || "").toUpperCase().trim() };
      else payload = { ...form };
      const { data } = await api.post(cfg.endpoint, payload);
      toast.success(`${cfg.title.replace("New ", "")} created`);
      setOpen(false); setForm({});
      onCreated?.(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button
        type="button" variant="outline"
        className={size === "icon" ? "h-9 w-9 shrink-0 p-0" : "h-8 shrink-0 px-2 text-xs"}
        data-testid={`quick-add-${type}-btn`}
        onClick={() => { setForm({ type: "Expense", brand: "Others" }); setOpen(true); }}
        title={cfg.title}
      >
        <Plus size={15} />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`quick-add-${type}-dialog`}>
          <DialogHeader><DialogTitle className="font-display">{cfg.title}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {cfg.fields.map(([k, label, req, kind]) => (
              <div key={k} className={k === "name" && type === "item" ? "col-span-2" : ""}>
                <Label className="lx-label">{label}{req ? " *" : ""}</Label>
                {kind === "brand" || kind === "acctype" || kind === "itemtype" ? (
                  <Select value={form[k] || (kind === "brand" ? "Others" : kind === "itemtype" ? "Goods" : "Expense")} onValueChange={(v) => setForm({ ...form, [k]: v })}>
                    <SelectTrigger className="mt-1" data-testid={`quick-add-${type}-${k}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{(kind === "brand" ? BRANDS : kind === "itemtype" ? ["Goods", "Service"] : ACCT_TYPES).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input className="mt-1" type={kind === "number" ? "number" : kind === "date" ? "date" : "text"} data-testid={`quick-add-${type}-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" disabled={saving} data-testid={`quick-add-${type}-save`} onClick={save}>{saving ? "Saving…" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
